// pages/purchases/index.tsx
"use client";
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';


import visaImg from "@/assets/images/purchases/visa.png";
import mastercardImg from "@/assets/images/purchases/mastercard.png";
import maestroImg from "@/assets/images/purchases/maestro.png";
import amexImg from "@/assets/images/purchases/american-express.png";
import discoverImg from "@/assets/images/purchases/discover.png";
import stripeImg from "@/assets/images/purchases/powered-by-stripe.svg";

interface OrderItem {
  title: string;
  is_tools: boolean;
  includes?: string[];
  monthly5?: string;
  monthly10?: string;
  monthly25?: string;
  monthly50?: string;
  yearly5?: string;
  yearly10?: string;
  yearly25?: string;
  yearly50?: string;
}

interface PurchasesProps {
  yourOrder?: string[];  // Changed to string[] to match the product keys
  products?: { [key: string]: OrderItem };
  showUpsell?: string | false;
  total?: string;
  billingPeriod?: string;
  employees?: string;
  portalPurchaseUrl?: string;
  quote?: {
    errors?: any[];
  };
  submissionError?: string;
}

// interface QuoteFormData {
//   name: string;
//   phone_number: string;
//   company: string;
//   email_address: string;
//   enquiry: string;
//   marketing: boolean;
// }

interface ProgressIndicatorProps {
  active: number;
  cssClass?: string;
  steps: string[];
}

// Add after the existing interfaces
type ProductCode = 'CE50' | 'CEP50' | 'CE_plus_50' | 'CE_plus_P50' | 'CET5' | 'CET10' | 'CET25' | 'CET50';

// Add the products constant before the PurchasesPage component
const PRODUCTS: { [key: string]: OrderItem } = {
  CE50: {
    title: "Cyber Essentials",
    is_tools: false,
    monthly5: "57.50",
    monthly10: "87.50",
    monthly25: "117.50",
    monthly50: "147.50",
    yearly5: "575",
    yearly10: "875",
    yearly25: "1175",
    yearly50: "1475",
    includes: [
      "Cyber Essentials certification",
      "Up to 25k FREE cyber insurance",
      "2 hrs Remote Support",
      "1 free retest",
    ]
  },
  CEP50: {
    title: "Cyber Essentials Premium",
    is_tools: false,
    monthly5: "97.50",
    monthly10: "127.50",
    monthly25: "157.50",
    monthly50: "187.50",
    yearly5: "975",
    yearly10: "1275",
    yearly25: "1575",
    yearly50: "1875",
    includes: [
      "Cyber Essentials certification",
      "Up to 25k FREE cyber insurance",
      "Tailored policy documents",
      "4 hrs Remote Support",
      "2 free retests",
    ]
  },
  CE_plus_50: {
    title: "Cyber Essentials Plus",
    is_tools: false,
    monthly5: "170.00",
    monthly10: "205",
    monthly25: "235",
    monthly50: "265",
    yearly5: "1750",
    yearly10: "2050",
    yearly25: "2350",
    yearly50: "2650",
    includes: [
      "Cyber Essentials certification",
      "Up to 25k FREE cyber insurance",
      "Cyber Essentials Plus certification",
      "4 hrs Remote Support",
      "1 free retest",
    ]
  },
  CE_plus_P50: {
    title: "Cyber Essentials Plus Premium",
    is_tools: false,
    monthly5: "210",
    monthly10: "240",
    monthly25: "270",
    monthly50: "300",
    yearly5: "2100",
    yearly10: "2400",
    yearly25: "2700",
    yearly50: "3000",
    includes: [
      "Cyber Essentials certification",
      "Up to 25k FREE cyber insurance",
      "Cyber Essentials Plus certification",
      "Tailored policy documents",
      "6 hrs Remote Support",
      "2 free retests",
    ]
  },
  CET5: {
    title: "Cyber tools for up to 5 employees",
    is_tools: true,
    includes: [
      "Anti-virus protection",
      "Training",
      "Vulnerability scanning",
      "Phishing simulator",
      "Threat dashboard",
      "Asset tracker",
      "+ more",
    ]
  },
  CET10: {
    title: "Cyber tools for up to 10 employees",
    is_tools: true,
    includes: [
      "Anti-virus protection",
      "Training",
      "Vulnerability scanning",
      "Phishing simulator",
      "Threat dashboard",
      "Asset tracker",
      "+ more",
    ]
  },
  CET25: {
    title: "Cyber tools for up to 25 employees",
    is_tools: true,
    includes: [
      "Anti-virus protection",
      "Training",
      "Vulnerability scanning",
      "Phishing simulator",
      "Threat dashboard",
      "Asset tracker",
      "+ more",
    ]
  },
  CET50: {
    title: "Cyber tools for up to 50 employees",
    is_tools: true,
    includes: [
      "Anti-virus protection",
      "Training",
      "Vulnerability scanning",
      "Phishing simulator",
      "Threat dashboard",
      "Asset tracker",
      "+ more",
    ]
  },
};


const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
                                                               active = 0,
                                                               cssClass = '',
                                                               steps
                                                             }) => {
  if (!steps || steps.length === 0) return null;

  const current = active + 1;
  const segmentSize = (1 / steps.length) * 100;

  return (
    <div
      className={`progress-indicator d-flex justify-content-center ${cssClass}`}
      data-total={steps.length}
      data-active={active}
      style={{ '--progress': `${current * segmentSize}%` } as React.CSSProperties}
    >
      <ol className="list-unstyled d-flex justify-content-center">
        {steps.map((step, index) => (
          <li
            key={`progress-step-${index}`}
            className={active === index ? 'on' : ''}
            id={`progress-indicator-step-${index}`}
          >
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
};

const PurchasesPage: React.FC<PurchasesProps> = ({
                                                   // yourOrder = [],
                                                   // products = {},
                                                   portalPurchaseUrl = "/purchase",
                                                   quote = {},
                                                   submissionError = ""
                                                 }) => {
  const searchParams = useSearchParams();
  const [orderItems, setOrderItems] = useState<string[]>([]);
  const [showUpsell, setShowUpsell] = useState<string | false>(false);
  const [employees, setEmployees] = useState('5');
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  const [total, setTotal] = useState('0');

  const steps = ['Customer Type', 'Payment', 'Confirmation'];
  const [activeStep] = useState(0);
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('new-customer');

  // const [formErrors, setFormErrors] = useState<string[]>([]);

  useEffect(() => {
    // Parse URL parameters
    const product = searchParams.get('product');
    const employeesParam = searchParams.get('employees');
    const billingPeriodParam = searchParams.get('billing_period');

    // Set billing period
    if (billingPeriodParam) {
      setBillingPeriod(billingPeriodParam);
    }

    // Set employees number
    if (employeesParam) {
      setEmployees(employeesParam);
    }

    // Parse product codes and set order items
    if (product) {
      const [productCode1, productCode2] = product.split(' ');
      const newOrderItems: string[] = [];
      let newShowUpsell: string | false = false;

      // Handle first product code
      switch (productCode1) {
        case 'CE50':
          newOrderItems.push('CE50');
          newShowUpsell = 'CEP50';
          break;
        case 'CEP50':
          newOrderItems.push('CEP50');
          break;
        case 'CE+50':
          newOrderItems.push('CE_plus_50');
          newShowUpsell = 'CE+P50';
          break;
        case 'CE+P50':
          newOrderItems.push('CE_plus_P50');
          break;
      }

      // Handle second product code (tools)
      if (productCode2) {
        switch (productCode2) {
          case 'CET5':
            newOrderItems.push('CET5');
            setEmployees('5');
            break;
          case 'CET10':
            newOrderItems.push('CET10');
            setEmployees('10');
            break;
          case 'CET25':
            newOrderItems.push('CET25');
            setEmployees('25');
            break;
          case 'CET50':
            newOrderItems.push('CET50');
            setEmployees('50');
            break;
        }
      }

      setOrderItems(newOrderItems);
      setShowUpsell(newShowUpsell);

      // Calculate total
      if (newOrderItems.length > 0) {
        let newTotal = 0;
        newOrderItems.forEach(item => {
          const product = PRODUCTS[item as ProductCode];
          if (product) {
            const priceKey = `${billingPeriodParam || billingPeriod}${employeesParam || employees}`;
            // Type guard to ensure the price exists and is a string
            if (
              (priceKey === 'monthly5' || priceKey === 'monthly10' ||
                priceKey === 'monthly25' || priceKey === 'monthly50' ||
                priceKey === 'yearly5' || priceKey === 'yearly10' ||
                priceKey === 'yearly25' || priceKey === 'yearly50') &&
              typeof product[priceKey] === 'string'
            ) {
              const price = product[priceKey] as string;
              newTotal += parseFloat(price);
            }
          }
        });
        setTotal(newTotal.toFixed(2));
      }
    }
  }, [searchParams, billingPeriod, employees]);


  useEffect(() => {
    // Find the radio input and set it as checked
    const defaultRadio = document.getElementById('new-customer') as HTMLInputElement;
    if (defaultRadio) {
      defaultRadio.checked = true;
    }

    // Form validation
    const forms = document.querySelectorAll('.needs-validation');

    const handleSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement;
      if (!form.checkValidity()) {
        event.preventDefault();
        event.stopPropagation();
      }
      form.classList.add('was-validated');
    };

    forms.forEach(form => {
      form.addEventListener('submit', handleSubmit);
    });

    return () => {
      forms.forEach(form => {
        form.removeEventListener('submit', handleSubmit);
      });
    };
  }, [billingPeriod, employees]);

  const customerTypes = [
    {
      id: 'new-customer',
      iconClass: 'fas fa-user fa-fw fa-2x',
      title: "I'm a new customer",
      description: "I'm a new customer buying Cyber Essentials for the first time"
    },
    {
      id: 'existing-customer',
      iconClass: 'fas fa-building fa-fw fa-2x',
      title: "I'm renewing Cyber Essentials",
      description: "I've previously purchased Cyber Essentials from Bulletproof"
    },
    {
      id: 'partner-customer',
      iconClass: 'fas fa-handshake fa-fw fa-2x',
      title: "I'm a partner buying for someone else",
      description: "I'm buying Cyber Essentials on behalf of another company"
    }
  ];

  const handleQuoteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Handle form submission logic here
  };

  return (
    <div className="container-lg">
      <div className="purchases-container">
        <ProgressIndicator
          active={activeStep}
          steps={steps}
          cssClass="mb-4"
        />

        <div className="card rounded-4 mb-5">
          <div className="row g-0">
            {/* Order Summary Column */}
            <div className="purchases-column-order col-24 col-md-8 col-lg-6 order-md-last">
              <div className="position-sticky sticky-top">
                <div className="p-4">
                  <h2 className="h3 mb-4">
                    <i className="fas fa-basket-shopping fa-fw fa-sm me-1"></i>
                    Your order
                  </h2>

                  <ol className="list-unstyled mb-4 purchases-basket">
                    {orderItems.length > 0 ? orderItems.map((productKey) => {
                      const product = PRODUCTS[productKey as ProductCode];
                      if (!product) return null;

                      return (
                        <React.Fragment key={productKey}>
                          <li className="mb-3">
                            <h3 className="h5 mb-1">{product.title}</h3>
                            {product.includes && product.includes.length > 0 && (
                              <ul className="list-unstyled list-checked small text-muted">
                                {product.includes.map((include, i) => (
                                  <li key={i}>{include}</li>
                                ))}
                              </ul>
                            )}
                          </li>
                          {!product.is_tools && showUpsell && (
                            <li className="mb-3 bg-blue-200 rounded-3 p-2">
                              <h3 className="h5 mb-1 text-secondary">Upgrade to Premium</h3>
                              <ul className="list-unstyled list-plus small text-muted mb-2">
                                <li>An extra 2 hours of remote support</li>
                                <li>An extra free retest</li>
                                <li>Tailored policy documents</li>
                              </ul>
                              <form action="/purchases" method="get" noValidate>
                                <input type="hidden" name="employees" value={employees} />
                                <input
                                  type="hidden"
                                  name="product"
                                  value={`${showUpsell} CET${employees}`}
                                />
                                <input type="hidden" name="billing_period" value={billingPeriod} />
                                <button type="submit" className="btn btn-sm btn-secondary d-block w-100">
                                  Upgrade to Premium
                                </button>
                              </form>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    }) : (
                      <li className="text-muted text-center">
                        No items in your basket
                      </li>
                    )}
                  </ol>

                  <div className="order-total d-flex justify-content-between align-items-end border-top pt-3">
                    <span className="h5 mb-0 text-dark text-uppercase">Total</span>
                    <span className="h3 mb-0 text-dark">
                      <span className="small">£</span>{total}
                    </span>
                  </div>

                  <div className="small text-muted text-end text-uppercase mb-4">
                    Billed {billingPeriod}
                  </div>

                  <div className="row text-center justify-content-center align-items-center mb-3">
                    <div className="col-24 col-md-auto">
                      {[
                        { src: visaImg, alt: "Visa" },
                        { src: mastercardImg, alt: "Mastercard" },
                        { src: maestroImg, alt: "Maestro" },
                        { src: amexImg, alt: "American Express" },
                        { src: discoverImg, alt: "Discover" }
                      ].map((card) => (
                        <Image
                          key={card.alt}
                          src={card.src}
                          width={32}
                          height={22}
                          className="img-fluid me-1 border rounded-3 d-inline-block"
                          alt={card.alt}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="row text-center justify-content-center align-items-center">
                    <div className="col-24 col-md-auto mb-3">
                      <i className="fas fa-lock fa-fw fa-lg me-2"></i>
                      Guaranteed <strong>safe &amp; secure checkout</strong>
                    </div>
                    <div className="col-24 col-md-auto">
                      <Image
                        src={stripeImg}
                        width={160}
                        height={44}
                        alt="Powered by Stripe"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Form Column */}
            <div className="purchases-column-main col-24 col-md-16 col-lg-18">
              <div className="p-4">
                <h2 className="h3 mb-4">Please tell us who you are</h2>

                {/* Customer Type Selection */}
                <div className="purchases-select-type row mb-4">
                  {customerTypes.map((type) => (
                    <div key={type.id} className="col-24 col-lg-8 position-relative mb-4 mb-lg-0">
                      <input
                        type="radio"
                        name="purchases-customer-type"
                        id={type.id}
                        value={type.id}
                        checked={selectedCustomerType === type.id}
                        onChange={(e) => setSelectedCustomerType(e.target.value)}
                      />
                      <label className="card card-lighter p-3 h-100 mb-4" htmlFor={type.id}>
                        <div className="text-primary mb-3">
                          <i className={type.iconClass}></i>
                        </div>
                        <h3 className="h4 text-primary">{type.title}</h3>
                        <p className="small text-muted mb-0">{type.description}</p>
                      </label>
                    </div>
                  ))}
                </div>

                {quote.errors && quote.errors.length > 0 && (
                  <div className="alert alert-danger">
                    {quote.errors.map((error, index) => (
                      <p key={index} className="mb-0">{error.message}</p>
                    ))}
                  </div>
                )}

                {submissionError && (
                  <div className="alert alert-danger">
                    <p className="mb-0">{submissionError}</p>
                  </div>
                )}

                {/* New Customer Form */}
                <div className={`border rounded-4 p-4 purchases-form ${selectedCustomerType === 'new-customer' ? '' : 'd-none'}`}>
                <h2 className="h3">New Customer</h2>
                  <p className="lead">We require this information so we can contact you to begin the certification process. This will happen once payment has been accepted.</p>
                  <p className="lead mb-4">Please provide a <strong>mobile number</strong> below. A password to allow you access our portal (where you complete your Cyber Essentials questionnaire) will be sent via SMS to this number.</p>
                  <hr className="mb-4" />
                  <form className="needs-validation" action={portalPurchaseUrl} method="post" noValidate>
                    <input type="hidden" name="employees" value={employees} />
                    <input type="hidden" name="product" value={showUpsell ? `${showUpsell} CET${employees}` : ''} />
                    <input type="hidden" name="billing_period" value={billingPeriod} />

                    {/* Name Field */}
                    <div className="row mb-3">
                      <div className="col-24">
                        <label className="form-label" htmlFor="purchase_full_name">Name *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="full_name"
                          id="purchase_full_name"
                        />
                      </div>
                    </div>

                    {/* Company and Job Title Fields */}
                    <div className="row mb-3">
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_company_name">Company Name *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="company_name"
                          id="purchase_company_name"
                        />
                      </div>
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_job_title">Job Title *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="job_title"
                          id="purchase_job_title"
                        />
                      </div>
                    </div>

                    {/* Contact Fields */}
                    <div className="row mb-4">
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_email_address">Email Address *</label>
                        <input
                          className="form-control"
                          required
                          type="email"
                          name="email_address"
                          id="purchase_email_address"
                        />
                      </div>
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_contact_number">Mobile Number *</label>
                        <input
                          className="form-control"
                          required
                          type="tel"
                          name="contact_number"
                          id="purchase_contact_number"
                        />
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div className="row mb-3">
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_address_line_1">Address Line 1 *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="address_line_1"
                          id="purchase_address_line_1"
                        />
                      </div>
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_address_line_2">Address Line 2</label>
                        <input
                          className="form-control"
                          type="text"
                          name="address_line_2"
                          id="purchase_address_line_2"
                        />
                      </div>
                    </div>

                    {/* Town/City and County Fields */}
                    <div className="row mb-3">
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_town_city">Town/City *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="town_city"
                          id="purchase_town_city"
                        />
                      </div>
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_county">County *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="county"
                          id="purchase_county"
                        />
                      </div>
                    </div>

                    {/* Postcode and Country Fields */}
                    <div className="row mb-4">
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_postcode">Postcode *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="postcode"
                          id="purchase_postcode"
                        />
                      </div>
                      <div className="col-24 col-lg-12">
                        <label className="form-label" htmlFor="purchase_country">Country *</label>
                        <input
                          className="form-control"
                          required
                          type="text"
                          name="country"
                          id="purchase_country"
                        />
                      </div>
                    </div>

                    {/* Terms Acceptance */}
                    <div className="row mb-3">
                      <div className="col-24 opt-in-checkbox opt-in-checkbox-dark text-center">
                        <div className="form-check">
                          <div className="d-inline-block mx-auto">
                            <input
                              required
                              className="form-check-input"
                              type="checkbox"
                              value="1"
                              name="terms_accepted"
                              id="purchase_terms"
                            />
                            <label className="form-check-label" htmlFor="purchase_terms">
                              I have read and understood the <Link href="/terms" target="_blank">terms and conditions</Link>
                            </label>
                            <div className="invalid-feedback">
                              You must agree before submitting.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row">
                      <div className="col-24">
                        <div className="text-center">
                          <button
                            type="submit"
                            className="btn btn-lg btn-primary btn-submit"
                            data-disable-with="Processing..."
                          >
                            Proceed to Payment
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                {/* Existing Customer Form */}
                <div className={`border rounded-4 p-4 purchases-form ${selectedCustomerType === 'existing-customer' ? '' : 'd-none'}`}>
                  <h2 className="h3">Existing Customer</h2>
                  <p className="lead mb-4">If you&apos;re already a Bulletproof Cyber Essentials customer – great! Thanks for choosing us for your renewal. Simply fill in this form and your account manager will get in touch to get you started on your new Cyber Essentials journey.</p>
                  <hr className="mb-4" />
                  <form onSubmit={handleQuoteSubmit} className="form-quote needs-validation" noValidate>
                    <div className="row gy-3 mb-3 mb-md-4">
                      {/* Name Field */}
                      <div className="col-md-12">
                        <label htmlFor="quote_name" className="form-label">Name *</label>
                        <input
                          type="text"
                          id="quote_name"
                          name="quote[name]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Phone Number Field */}
                      <div className="col-md-12">
                        <label htmlFor="quote_phone_number" className="form-label">Phone Number *</label>
                        <input
                          type="tel"
                          id="quote_phone_number"
                          name="quote[phone_number]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Company Name Field */}
                      <div className="col-md-12">
                        <label htmlFor="quote_company" className="form-label">Company Name *</label>
                        <input
                          type="text"
                          id="quote_company"
                          name="quote[company]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Email Address Field */}
                      <div className="col-md-12">
                        <label htmlFor="quote_email_address" className="form-label">Email Address *</label>
                        <input
                          type="email"
                          id="quote_email_address"
                          name="quote[email_address]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Hidden Fields */}
                      <input type="hidden" name="quote[service]" value="Cyber Essentials" />
                      <input
                        type="hidden"
                        name="quote[how_did_you_find_us]"
                        value={`This customer is an EXISTING CUSTOMER. Please contact regarding a Cyber Essentials purchase: ${showUpsell ? `${showUpsell} CET${employees}` : ''}`}
                      />

                      {/* Enquiry Field */}
                      <div className="col-md-24">
                        <label htmlFor="quote_enquiry" className="form-label">Information on your enquiry *</label>
                        <small className="char-limit">(1,500 characters limit)</small>
                        <textarea
                          id="quote_enquiry"
                          name="quote[enquiry]"
                          rows={4}
                          maxLength={1500}
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Marketing Opt-in */}
                      <div className="col-md-24 gy-3 gy-md-4">
                        <p className="mb-1">
                          <label>
                            <input
                              type="checkbox"
                              name="quote[marketing]"
                              className="me-2"
                            />
                            I&apos;d like to receive Bulletproof communications about relevant services and events
                          </label>
                        </p>
                        <p className="mb-0">
                          For more information about how we collect, process and retain your personal data, please see our{' '}
                          <Link href="/privacy-notice" target="_blank">privacy policy</Link>.
                        </p>
                      </div>
                    </div>

                    {/* Hidden Analytics ID */}
                    <input type="hidden" name="quote[analytics_id]" className="analytics_id" value="" />

                    {/* Submit Button */}
                    <div>
                      <button
                        type="submit"
                        className="btn btn-lg btn-primary"
                      >
                        Start my Cyber Essentials renewal
                      </button>
                    </div>
                  </form>
                </div>

                {/* Partner Form */}
                <div className={`border rounded-4 p-4 purchases-form ${selectedCustomerType === 'partner-customer' ? '' : 'd-none'}`}>
                  <h2 className="h3">Partner</h2>
                  <p className="lead mb-4">If you&apos;re a partner or reseller looking to buy Cyber Essentials for another organisation, please get in touch with us using the form below. Please enter the details of your partner organisation, not the end user company.</p>
                  <hr className="mb-4" />
                  <form onSubmit={handleQuoteSubmit} className="form-quote needs-validation" noValidate>
                    <div className="row gy-3 mb-3 mb-md-4">
                      {/* Name Field */}
                      <div className="col-md-12">
                        <label htmlFor="partner_quote_name" className="form-label">Name *</label>
                        <input
                          type="text"
                          id="partner_quote_name"
                          name="quote[name]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Phone Number Field */}
                      <div className="col-md-12">
                        <label htmlFor="partner_quote_phone_number" className="form-label">Phone Number *</label>
                        <input
                          type="tel"
                          id="partner_quote_phone_number"
                          name="quote[phone_number]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Company Name Field */}
                      <div className="col-md-12">
                        <label htmlFor="partner_quote_company" className="form-label">Partner Company Name *</label>
                        <input
                          type="text"
                          id="partner_quote_company"
                          name="quote[company]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Email Address Field */}
                      <div className="col-md-12">
                        <label htmlFor="partner_quote_email_address" className="form-label">Email Address *</label>
                        <input
                          type="email"
                          id="partner_quote_email_address"
                          name="quote[email_address]"
                          className="form-control"
                          required
                        />
                      </div>

                      {/* Hidden Fields */}
                      <input type="hidden" name="quote[service]" value="Cyber Essentials" />
                      <input
                        type="hidden"
                        name="quote[how_did_you_find_us]"
                        value={`This customer is a PARTNER. Please contact regarding a Cyber Essentials purchase: ${showUpsell ? `${showUpsell} CET${employees}` : ''}`}
                      />

                      {/* Enquiry Field */}
                      <div className="col-md-24">
                        <label htmlFor="partner_quote_enquiry" className="form-label">Information about the end user company *</label>
                        <small className="char-limit d-block mb-2">(1,500 characters limit)</small>
                        <textarea
                          id="partner_quote_enquiry"
                          name="quote[enquiry]"
                          rows={4}
                          maxLength={1500}
                          className="form-control"
                          placeholder="Please provide details about the end user company that requires Cyber Essentials certification"
                          required
                        />
                      </div>

                      {/* Marketing Opt-in */}
                      <div className="col-md-24 gy-3 gy-md-4">
                        <p className="mb-1">
                          <label className="d-flex align-items-start">
                            <input
                              type="checkbox"
                              name="quote[marketing]"
                              className="me-2 mt-1"
                            />
                            <span>
              I&apos;d like to receive Bulletproof communications about relevant services and events
            </span>
                          </label>
                        </p>
                        <p className="mb-0">
                          For more information about how we collect, process and retain your personal data, please see our{' '}
                          <Link href="/privacy-notice" target="_blank">privacy policy</Link>.
                        </p>
                      </div>
                    </div>

                    {/* Hidden Analytics ID */}
                    <input type="hidden" name="quote[analytics_id]" className="analytics_id" value="" />

                    {/* Submit Button */}
                    <div>
                      <button
                        type="submit"
                        className="btn btn-lg btn-primary"
                      >
                        Get started with Cyber Essentials
                      </button>
                    </div>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasesPage;